import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api, type Agent, type ChatMessage, type ChatThreadSummary, type ChatThreadMode, type ProjectListItem, type EphemeralEnv, type Report } from '../api';
import { Page, AgentAvatar, GenAvatar, Markdown, ChatTabs } from '../ui';
import { parseDocBlocks, type ProposedDoc } from '../lib/docBlocks';

// Escolha do seletor de "Nova conversa": um agente específico (id), o
// recepcionista ('__router__'), ou o chat geral sobre o projeto ('').
type AgentChoice = string;

interface StreamingAssistantState {
  text: string;
  tools: Array<{ name: string; input?: unknown; ok?: boolean }>;
}

export function Chat() {
  const { slug, threadId } = useParams<{ slug?: string; threadId?: string }>();
  const nav = useNavigate();

  const [threads, setThreads] = useState<ChatThreadSummary[]>([]);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  // Threads scoped to the current project. Other-project threads stay hidden.
  const projectThreads = useMemo(
    () => (slug ? threads.filter((t) => t.projectSlug === slug) : threads),
    [threads, slug],
  );
  const project = useMemo(
    () => (slug ? projects.find((p) => p.slug === slug) ?? null : null),
    [projects, slug],
  );
  // Reports já despachados nesta thread (título → reportId), das msgs de sistema —
  // pra o card de despacho saber que já foi despachado (mostra tracker, não o botão).
  const dispatchedReports = useMemo(() => {
    const m = new Map<string, string>();
    for (const msg of messages) {
      const d = msg.toolInfo?.['dispatch'] as { reportId?: string } | undefined;
      if (msg.role === 'system' && d?.reportId) {
        const title = msg.content.replace(/^Report despachado:\s*/, '').trim();
        if (title) m.set(title, d.reportId);
      }
    }
    return m;
  }, [messages]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState<StreamingAssistantState | null>(null);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Agentes do projeto atual + escolha pra próxima conversa.
  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentChoice, setAgentChoice] = useState<AgentChoice>('__router__');
  const [searchParams] = useSearchParams();
  // Em modo router, qual agente o recepcionista despachou (banner na conversa).
  const [routedAgent, setRoutedAgent] = useState<string | null>(null);
  // Quando o agente está consultando outro agente (delegação).
  const [consulting, setConsulting] = useState<string | null>(null);

  // Refs usados pelo stream pra evitar recriação dos handlers
  const streamingRef = useRef<StreamingAssistantState | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const reconnectRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Ids de mensagens que CHEGARAM nesta sessão (ao vivo) — só nelas os cartões de
  // banco auto-executam. Evita re-rodar queries (e INSERTs!) ao reabrir o histórico.
  const liveIdsRef = useRef<Set<string>>(new Set());
  // Sempre aponta pra versão MAIS RECENTE de sendText. Os cartões guardam a referência
  // do render em que montaram (quando sending ainda era true) — usar a ref evita a
  // guarda obsoleta matar a continuação automática após aprovar um acesso.
  const sendTextRef = useRef<(text: string) => void>(() => {});

  // Carregar lista de threads + projetos
  useEffect(() => {
    Promise.all([api.listThreads(), api.listProjects()])
      .then(([t, p]) => {
        setThreads(t.threads);
        setProjects(p.projects);
      })
      .catch(() => setErr('Falha ao carregar'));
  }, []);

  // Carregar agentes do projeto atual (pra seletor de Nova conversa).
  useEffect(() => {
    if (!slug) {
      setAgents([]);
      return;
    }
    api
      .listAgents(slug)
      .then(({ agents }) => {
        const list = agents.filter((a) => a.enabled && !a.isRouter);
        setAgents(list);
        // Deep-link: /p/<slug>/chat?agent=<slug|id> pré-seleciona o agente.
        const want = searchParams.get('agent');
        if (want) {
          const match = list.find((a) => a.slug === want || a.id === want);
          if (match) setAgentChoice(match.id);
        }
      })
      .catch(() => setAgents([]));
  }, [slug, searchParams]);

  // Carregar mensagens da thread atual. Se houver um turno em andamento (o agente
  // ainda respondendo), reconecta ao stream e mostra o progresso ao vivo.
  useEffect(() => {
    if (!threadId) {
      setMessages([]);
      return;
    }
    setRoutedAgent(null);
    setStreaming(null);
    streamingRef.current = null;
    setConsulting(null);
    setErr(null);
    const ac = new AbortController();
    reconnectRef.current = ac;
    let cancelled = false;

    api
      .getThread(threadId)
      .then(({ messages, running }) => {
        if (cancelled) return;
        setMessages(messages);
        if (running) reconnect(threadId, ac);
      })
      .catch(() => {
        if (!cancelled) setErr('Falha ao carregar thread');
      });

    return () => {
      cancelled = true;
      ac.abort(); // encerra a reconexão deste thread
      abortRef.current?.abort(); // e o POST em andamento (o turno continua no servidor)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, streaming]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  async function createThread(projectId: string | null) {
    // Traduz a escolha do seletor em (agentId, mode).
    let agentId: string | null = null;
    let mode: ChatThreadMode | null = null;
    if (projectId && agentChoice === '__router__') {
      mode = 'router';
    } else if (projectId && agentChoice && agentChoice !== '') {
      agentId = agentChoice;
      mode = 'direct';
    }

    const { thread } = await api.createThread({ projectId, agentId, mode });
    setThreads((prev) => [
      {
        id: thread.id,
        title: thread.title,
        projectId: thread.projectId,
        projectSlug: projects.find((p) => p.id === thread.projectId)?.slug ?? null,
        projectName: projects.find((p) => p.id === thread.projectId)?.name ?? null,
        agentId: thread.agentId,
        agentName: agents.find((a) => a.id === thread.agentId)?.name ?? null,
        mode: thread.mode,
        createdAt: thread.createdAt,
        updatedAt: thread.updatedAt,
      },
      ...prev,
    ]);
    nav(slug ? `/p/${slug}/chat/${thread.id}` : `/chat/${thread.id}`);
  }

  async function deleteThread(id: string) {
    if (!confirm('Apagar esta conversa?')) return;
    await api.deleteThread(id);
    setThreads((prev) => prev.filter((t) => t.id !== id));
    if (threadId === id) nav(slug ? `/p/${slug}/chat` : '/chat');
  }

  async function send() {
    const content = input.trim();
    if (!content || sending || streaming) return;
    setInput('');
    await sendText(content);
  }

  // Envia um texto como mensagem do usuário e consome o stream da resposta.
  // Usado pelo input (send) e por continuações automáticas (ex.: após aprovar
  // um acesso ao banco, o agente prossegue sozinho com a operação pedida).
  async function sendText(content: string) {
    if (!threadId || !content || sending || streaming) return;
    setSending(true);
    setErr(null);
    setConsulting(null);

    const userMsgId = `tmp-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: userMsgId,
        threadId,
        role: 'user',
        content,
        toolInfo: null,
        createdAt: new Date().toISOString(),
      },
    ]);

    const init: StreamingAssistantState = { text: '', tools: [] };
    streamingRef.current = init;
    setStreaming(init);

    const ac = new AbortController();
    abortRef.current = ac;

    try {
      const res = await fetch(`/v1/chat/threads/${threadId}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ content }),
        signal: ac.signal,
      });
      if (!res.ok || !res.body) {
        setErr(`Erro HTTP ${res.status}`);
        streamingRef.current = null;
        setStreaming(null);
        return;
      }

      await consumeSse(res.body, (event, data) => {
        if (event === 'done') {
          setConsulting(null);
          const s = streamingRef.current;
          if (!s) return;
          const finalText = data['finalText'];
          if (typeof finalText === 'string' && finalText) s.text = finalText;
          appendAssistant(threadId, s);
          streamingRef.current = null;
          setStreaming(null);
        } else {
          applyProgress(event, data);
        }
      });
    } catch (e) {
      if (!ac.signal.aborted) setErr(String(e));
      streamingRef.current = null;
      setStreaming(null);
    } finally {
      setSending(false);
    }
  }
  sendTextRef.current = sendText; // mantém a ref na versão atual (closure fresco)

  // Aplica os eventos de PROGRESSO comuns (texto, ferramentas, roteamento, consulta).
  // Compartilhado pelo envio (POST) e pela reconexão (GET /stream).
  function applyProgress(event: string, data: Record<string, unknown>) {
    if (event === 'title') {
      const title = String(data['title'] ?? '').trim();
      if (title && threadId) setThreads((prev) => prev.map((t) => (t.id === threadId ? { ...t, title } : t)));
    } else if (event === 'agent_selected') {
      setRoutedAgent(String(data['name'] ?? ''));
    } else if (event === 'consulting') {
      setConsulting(String(data['agent'] ?? ''));
    } else if (event === 'text_delta') {
      setConsulting(null);
      const s = streamingRef.current;
      if (!s) return;
      s.text += String(data['delta'] ?? '');
      setStreaming({ ...s });
    } else if (event === 'tool_use') {
      const s = streamingRef.current;
      if (!s) return;
      s.tools.push({ name: String(data['name']), input: data['input'] });
      setStreaming({ ...s });
    } else if (event === 'tool_result') {
      const s = streamingRef.current;
      if (!s) return;
      const name = String(data['name']);
      const last = [...s.tools].reverse().find((t) => t.name === name && t.ok === undefined);
      if (last) last.ok = !!data['ok'];
      setStreaming({ ...s });
    } else if (event === 'error') {
      setErr(String(data['message'] ?? 'erro'));
    }
  }

  function appendAssistant(tid: string, s: StreamingAssistantState) {
    const id = `asst-${Date.now()}`;
    liveIdsRef.current.add(id); // mensagem ao vivo: cartões de banco podem auto-executar
    setMessages((prev) => [
      ...prev,
      {
        id,
        threadId: tid,
        role: 'assistant',
        content: s.text,
        toolInfo: s.tools.length > 0 ? { tools: s.tools } : null,
        createdAt: new Date().toISOString(),
      },
    ]);
  }

  // Reconecta a um turno em andamento ao reabrir a conversa: mostra todo o progresso
  // já ocorrido (replay) + o que vier ao vivo. Ao terminar, recarrega do banco (a
  // resposta final já foi persistida) — assim não duplica nem perde nada.
  async function reconnect(tid: string, ac: AbortController) {
    const init: StreamingAssistantState = { text: '', tools: [] };
    streamingRef.current = init;
    setStreaming(init);
    try {
      const res = await fetch(`/v1/chat/threads/${tid}/stream`, { credentials: 'include', signal: ac.signal });
      if (!res.ok || !res.body) return;
      await consumeSse(res.body, (event, data) => {
        if (event === 'done') return; // não anexa local; recarrega do banco no fim
        applyProgress(event, data);
      });
    } catch {
      /* abortado ou rede — ok */
    } finally {
      if (!ac.signal.aborted) {
        streamingRef.current = null;
        setStreaming(null);
        setConsulting(null);
        api.getThread(tid).then(({ messages }) => setMessages(messages)).catch(() => undefined);
      }
    }
  }

  const activeThread = threads.find((t) => t.id === threadId);
  const visibleThreads = projectThreads;
  // Agente da conversa (pra personalizar: avatar, saudação, cor de destaque).
  const chatAgent = activeThread?.agentId
    ? agents.find((a) => a.id === activeThread.agentId)
    : routedAgent
      ? agents.find((a) => a.name === routedAgent)
      : undefined;
  const chatAccent = chatAgent?.accentColor || undefined;

  return (
    <Page
      title="Chat"
      subtitle={
        project
          ? `Pergunte sobre o código de ${project.name}. O Claude lê os arquivos sob demanda.`
          : 'Pergunte sobre o projeto. O Claude lê o código sob demanda.'
      }
      fluid
    >
    {slug && <ChatTabs slug={slug} />}
    <div className="chat-layout">
      <aside className="chat-sidebar">
        <div className="chat-new">
          {project && slug && (
            <AgentPicker slug={slug} agents={agents} value={agentChoice} onChange={setAgentChoice} />
          )}
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: '100%' }}
            onClick={() => createThread(project?.id ?? null)}
          >
            + Nova conversa
          </button>
        </div>
        <div className="chat-thread-list">
          {visibleThreads.map((t) => (
            <Link
              key={t.id}
              to={slug ? `/p/${slug}/chat/${t.id}` : `/chat/${t.id}`}
              className={`chat-thread-item ${t.id === threadId ? 'active' : ''}`}
            >
              <div className="chat-thread-title">{t.title}</div>
              <div className="chat-thread-meta">{formatRelative(t.updatedAt)}</div>
              <button
                type="button"
                className="chat-thread-delete"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  deleteThread(t.id);
                }}
                aria-label="apagar"
              >
                ×
              </button>
            </Link>
          ))}
          {visibleThreads.length === 0 && (
            <div className="chat-empty">Nenhuma conversa nesse projeto ainda.</div>
          )}
        </div>
      </aside>

      <section className="chat-main" style={chatAccent ? ({ '--chat-accent': chatAccent } as CSSProperties) : undefined}>
        {!threadId ? (
          <div className="chat-placeholder">
            <h2>{project ? `Chat — ${project.name}` : 'Chat'}</h2>
            <p>Comece clicando em "Nova conversa" à esquerda.</p>
            <p style={{ color: 'var(--color-fg-muted)', fontSize: 12 }}>
              {project
                ? 'O Claude tem acesso ao repositório, premissas e config.yaml deste projeto.'
                : 'Sem projeto, responde só perguntas gerais.'}
            </p>
          </div>
        ) : (
          <>
            <header className="chat-header">
              <div className="chat-header-id">
                <ChatHeaderAvatar slug={slug} agents={agents} thread={activeThread} routedAgent={routedAgent} />
                <div>
                  <div className="chat-header-title">{activeThread?.title ?? 'conversa'}</div>
                  <div className="chat-header-meta">
                    {activeThread?.agentName
                      ? activeThread.agentName
                      : activeThread?.mode === 'router'
                        ? (routedAgent ? `🧭 ${routedAgent}` : '🧭 Recepcionista')
                        : (activeThread?.projectName ?? 'sem projeto')}
                  </div>
                </div>
              </div>
            </header>
            <div className="chat-messages" ref={scrollRef}>
              {chatAgent && (
                <div className="chat-intro">
                  <AgentAvatar slug={slug!} agent={chatAgent} size={56} />
                  <div className="chat-intro-text">
                    <div className="chat-intro-name">{chatAgent.name}</div>
                    <div className="chat-intro-greet">{chatAgent.greeting?.trim() || chatAgent.description || 'Como posso te ajudar?'}</div>
                  </div>
                </div>
              )}
              {activeThread?.mode === 'router' && routedAgent && (
                <div className="chat-routed-banner">Recepcionista encaminhou para: <strong>{routedAgent}</strong></div>
              )}
              {messages
                .filter((m) => m.status !== 'streaming') // o parcial ao vivo é mostrado pelo StreamingView
                .map((m) => (
                  <MessageView key={m.id} message={m} slug={slug} threadId={threadId} agentId={activeThread?.agentId ?? agents.find((a) => a.name === routedAgent)?.id} live={liveIdsRef.current.has(m.id)} onContinue={(t) => sendTextRef.current(t)} dispatchedReports={dispatchedReports} />
                ))}
              {consulting && (
                <div className="chat-routed-banner">🔗 consultando <strong>{consulting}</strong>…</div>
              )}
              {streaming && (
                <StreamingView state={streaming} />
              )}
              {err && <div className="chat-error">{err}</div>}
            </div>
            <div className="chat-input-row">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={streaming ? 'o agente está respondendo…' : 'Pergunte algo… (Enter envia, Shift+Enter quebra linha)'}
                rows={3}
                disabled={sending || streaming != null}
              />
              <button type="button" onClick={send} disabled={sending || streaming != null || !input.trim()}>
                {sending || streaming ? 'respondendo…' : 'enviar'}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
    </Page>
  );
}

interface DispatchProposal {
  title: string;
  description: string;
  kind: 'bug' | 'suggestion';
  baseBranch?: string | undefined;
  wantsEphemeral?: boolean | undefined;
}

interface EnvUp { branch: string; ttlMinutes?: number | undefined }
interface EnvDown { envId: string }
interface LogsReq { container: string; day?: string | undefined; q?: string | undefined }
interface DbQueryReq { env: string; query: string }
interface GrantProposalReq { env: string; operations: string[]; tables: string[]; rationale?: string | undefined; query?: string | undefined }
interface ParsedActions {
  text: string;
  proposal: DispatchProposal | null;
  envUp: EnvUp | null;
  envDown: EnvDown | null;
  logs: LogsReq | null;
  docs: ProposedDoc[];
  dbQuery: DbQueryReq | null;
  grantProposal: GrantProposalReq | null;
}

function pickBlock(content: string, tag: string): { json: Record<string, unknown> | null; stripped: string } {
  const m = content.match(new RegExp('```' + tag + '\\s*([\\s\\S]*?)```'));
  if (!m) return { json: null, stripped: content };
  let json: Record<string, unknown> | null = null;
  try {
    json = JSON.parse((m[1] ?? '').trim()) as Record<string, unknown>;
  } catch {
    /* ignore */
  }
  return { json, stripped: content.replace(m[0], '').trim() };
}

/** Extrai os blocos de ação (dispatch / env-up / env-down) e retorna o texto limpo. */
function parseActions(content: string): ParsedActions {
  let text = content;
  const d = pickBlock(text, 'bugpilot-dispatch');
  text = d.stripped;
  const u = pickBlock(text, 'bugpilot-env-up');
  text = u.stripped;
  const dn = pickBlock(text, 'bugpilot-env-down');
  text = dn.stripped;
  const lg = pickBlock(text, 'bugpilot-logs');
  text = lg.stripped;
  const cs = pickBlock(text, 'bugpilot-consult'); // só controle — esconde da conversa
  text = cs.stripped;
  const dq = pickBlock(text, 'bugpilot-query');
  text = dq.stripped;
  const gp = pickBlock(text, 'bugpilot-grant-proposal');
  text = gp.stripped;
  const docBlocks = parseDocBlocks(text); // docs propostos (4 crases)
  text = docBlocks.stripped;

  const proposal =
    d.json && typeof d.json['title'] === 'string' && typeof d.json['description'] === 'string'
      ? {
          title: d.json['title'] as string,
          description: d.json['description'] as string,
          kind: d.json['kind'] === 'bug' ? ('bug' as const) : ('suggestion' as const),
          baseBranch: typeof d.json['baseBranch'] === 'string' && d.json['baseBranch'] ? (d.json['baseBranch'] as string) : undefined,
          wantsEphemeral: d.json['wantsEphemeral'] === true ? true : undefined,
        }
      : null;
  const envUp = u.json && typeof u.json['branch'] === 'string'
    ? { branch: u.json['branch'] as string, ttlMinutes: typeof u.json['ttlMinutes'] === 'number' ? (u.json['ttlMinutes'] as number) : undefined }
    : null;
  const envDown = dn.json && typeof dn.json['envId'] === 'string' ? { envId: dn.json['envId'] as string } : null;
  const logs = lg.json && typeof lg.json['container'] === 'string'
    ? {
        container: lg.json['container'] as string,
        day: typeof lg.json['day'] === 'string' ? (lg.json['day'] as string) : undefined,
        q: typeof lg.json['q'] === 'string' ? (lg.json['q'] as string) : undefined,
      }
    : null;

  const dbQuery = dq.json && typeof dq.json['env'] === 'string' && typeof dq.json['query'] === 'string'
    ? { env: dq.json['env'] as string, query: dq.json['query'] as string }
    : null;

  const grantProposal = gp.json && typeof gp.json['env'] === 'string' && Array.isArray(gp.json['tables'])
    ? {
        env: gp.json['env'] as string,
        operations: Array.isArray(gp.json['operations']) ? (gp.json['operations'] as unknown[]).map(String) : [],
        tables: (gp.json['tables'] as unknown[]).map(String),
        rationale: typeof gp.json['rationale'] === 'string' ? (gp.json['rationale'] as string) : undefined,
        query: typeof gp.json['query'] === 'string' && gp.json['query'] ? (gp.json['query'] as string) : undefined,
      }
    : null;

  return { text: text.trim(), proposal, envUp, envDown, logs, docs: docBlocks.docs, dbQuery, grantProposal };
}

function ChatHeaderAvatar({
  slug, agents, thread, routedAgent,
}: { slug?: string | undefined; agents: Agent[]; thread?: ChatThreadSummary | undefined; routedAgent: string | null }) {
  if (!thread) return null;
  let agent: Agent | undefined;
  if (thread.agentId) agent = agents.find((a) => a.id === thread.agentId);
  else if (routedAgent) agent = agents.find((a) => a.name === routedAgent);
  if (agent && slug) return <AgentAvatar slug={slug} agent={agent} size={36} />;
  const name = thread.agentName || routedAgent || (thread.mode === 'router' ? 'Recepcionista' : 'Chat');
  return <GenAvatar name={name} size={36} />;
}

function MessageView({ message, slug, threadId, agentId, live, onContinue, dispatchedReports }: { message: ChatMessage; slug?: string | undefined; threadId?: string | undefined; agentId?: string | undefined; live?: boolean | undefined; onContinue?: ((text: string) => void) | undefined; dispatchedReports?: Map<string, string> | undefined }) {
  const tools = useMemo(() => {
    const raw = message.toolInfo?.['tools'];
    return Array.isArray(raw) ? (raw as Array<{ name: string; ok?: boolean }>) : [];
  }, [message.toolInfo]);

  // Reporte de resultado de banco devolvido ao agente: o output já apareceu no
  // card acima, então aqui mostramos só uma linha discreta (não a bolha crua).
  if (message.role === 'user' && /^\[(resultado da execução|erro ao executar) no banco/.test(message.content)) {
    const isErr = message.content.startsWith('[erro');
    return (
      <div className="chat-dispatch-done" style={{ opacity: 0.65 }}>
        ↳ {isErr ? 'erro do banco' : 'resultado do banco'} devolvido ao agente
      </div>
    );
  }

  // Mensagem de sistema marcando um dispatch já feito.
  const dispatched = message.toolInfo?.['dispatch'] as { reportId?: string; kind?: string } | undefined;
  if (message.role === 'system' && dispatched?.reportId) {
    // O tracker vivo é mostrado pelo próprio card de despacho (que detecta o report
    // pelo título via dispatchedReports). Esta msg de sistema vira ruído → oculta.
    return null;
  }

  const parsed = message.role === 'assistant'
    ? parseActions(message.content)
    : { text: message.content, proposal: null, envUp: null, envDown: null, logs: null, docs: [] as ProposedDoc[], dbQuery: null, grantProposal: null };

  return (
    <div className={`chat-msg chat-msg-${message.role}`}>
      <div className="chat-msg-role">{message.role === 'user' ? 'você' : 'assistente'}</div>
      <div className="chat-msg-content">
        {message.role === 'assistant'
          ? (parsed.text ? <Markdown text={parsed.text} /> : (message.status === 'error' ? <span style={{ color: 'var(--muted)' }}>(sem resposta)</span> : '…'))
          : parsed.text}
      </div>
      {message.role === 'assistant' && message.status === 'error' && (
        <div className="chat-interrupted">⚠️ resposta interrompida (a sessão caiu). Envie novamente para continuar.</div>
      )}
      {tools.length > 0 && (
        <div className="chat-tool-list">
          {tools.map((t, i) => (
            <span key={i} className="chat-tool-chip">
              {t.name} {t.ok === false ? '⚠' : t.ok ? '✓' : '…'}
            </span>
          ))}
        </div>
      )}
      {parsed.proposal && threadId && <DispatchCard proposal={parsed.proposal} threadId={threadId} slug={slug} dispatchedReportId={dispatchedReports?.get(parsed.proposal.title)} />}
      {parsed.envUp && slug && <EnvActionCard slug={slug} action={{ type: 'up', ...parsed.envUp }} />}
      {parsed.envDown && slug && <EnvActionCard slug={slug} action={{ type: 'down', ...parsed.envDown }} />}
      {parsed.logs && slug && <LogsRequestCard slug={slug} req={parsed.logs} />}
      {slug && parsed.docs.map((d, i) => <DocSaveCard key={i} slug={slug} doc={d} />)}
      {parsed.dbQuery && slug && <DbQueryCard slug={slug} agentId={agentId} req={parsed.dbQuery} autoRun={!!live} onContinue={onContinue} />}
      {parsed.grantProposal && slug && <GrantProposalCard slug={slug} agentId={agentId} req={parsed.grantProposal} onApproved={onContinue} />}
    </div>
  );
}

// Mensagem que o card devolve ao agente após executar no banco — ele resume
// pro usuário (e reage a erros). O output vai truncado pra não estourar contexto.
function dbResultMessage(env: string, ok: boolean, payload: string): string {
  const body = payload.length > 2500 ? `${payload.slice(0, 2500)}\n…(truncado)` : payload;
  return ok
    ? `[resultado da execução no banco · ${env}]\n${body}\n\n(explique pro usuário, em linguagem natural, o que aconteceu — e siga com o próximo passo se houver. Se "deu boa", guarde o script em docs/scripts/ conforme combinado.)`
    : `[erro ao executar no banco · ${env}]\n${body}\n\n(explique pro usuário o que deu errado e proponha como resolver — sem repetir a mesma query cega.)`;
}

/** Cartão: consulta de banco proposta por um agente (passa pelo broker seguro). */
function DbQueryCard({ slug, agentId, req, autoRun, onContinue }: { slug: string; agentId?: string | undefined; req: { env: string; query: string }; autoRun?: boolean | undefined; onContinue?: ((text: string) => void) | undefined }) {
  const [state, setState] = useState<'idle' | 'running' | 'done' | 'error' | 'confirm'>('idle');
  const [output, setOutput] = useState('');
  const [err, setErr] = useState('');
  const ranRef = useRef(false);
  const reportedRef = useRef(false);

  // Fecha o laço: devolve o resultado (ou erro) ao agente pra ele explicar em
  // linguagem natural e seguir com o próximo passo. Uma vez por card.
  function reportToAgent(ok: boolean, payload: string) {
    if (reportedRef.current || !onContinue) return;
    reportedRef.current = true;
    onContinue(dbResultMessage(req.env, ok, payload));
  }

  // Mensagem ao vivo: executa sozinho assim que aparece. Leitura sempre roda;
  // escrita só roda direto em ambiente confiado — senão o broker pede confirmação.
  useEffect(() => {
    if (autoRun && !ranRef.current && agentId) {
      ranRef.current = true;
      void run(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun]);

  async function run(confirm = false) {
    if (!agentId) {
      setErr('Sem agente nesta conversa (consulta de banco só em conversa com um agente específico).');
      setState('error');
      return;
    }
    setState('running');
    setErr('');
    try {
      // resolve o ambiente pelo tier ou nome.
      const { environments } = await api.listEnvironments(slug);
      const wanted = req.env.trim().toLowerCase();
      const env = environments.find((e) => (e.tier ?? '').toLowerCase() === wanted || e.name.toLowerCase() === wanted)
        ?? environments.find((e) => e.name.toLowerCase().includes(wanted));
      if (!env) {
        setErr(`Ambiente "${req.env}" não encontrado. Configure em Ambientes.`);
        setState('error');
        return;
      }
      const r = await api.dbQuery(slug, { agentId, environmentId: env.id, query: req.query, confirm });
      if (r.ok) {
        setOutput(r.output);
        setState('done');
        reportToAgent(true, r.output);
      } else if (r.needsConfirm) {
        setState('confirm');
      } else {
        setErr(r.error);
        setState('error');
        reportToAgent(false, r.error);
      }
    } catch (e) {
      setErr((e as { error?: string }).error ?? 'Falha na consulta.');
      setState('error');
    }
  }

  // Confirma a escrita E marca o ambiente como confiado (próximas escritas aqui
  // rodam sem confirmação). Preserva o escopo do grant atual, só desliga o confirm.
  async function trustAndRun() {
    if (!agentId) { setErr('Sem agente nesta conversa.'); setState('error'); return; }
    setState('running'); setErr('');
    try {
      const { environments, grants } = await api.listAgentDbGrants(slug, agentId);
      const wanted = req.env.trim().toLowerCase();
      const env = environments.find((e) => (e.tier ?? '').toLowerCase() === wanted || e.name.toLowerCase() === wanted)
        ?? environments.find((e) => e.name.toLowerCase().includes(wanted));
      if (!env) { setErr(`Ambiente "${req.env}" não encontrado.`); setState('error'); return; }
      const g = grants.find((x) => x.environmentId === env.id);
      if (g) {
        await api.saveAgentDbGrant(slug, agentId, env.id, {
          operations: g.operations, tables: g.tables, maxRows: g.maxRows,
          requireConfirmWrite: false, instructions: g.instructions, enabled: true,
        });
      }
      const r = await api.dbQuery(slug, { agentId, environmentId: env.id, query: req.query, confirm: true });
      if (r.ok) { setOutput(r.output); setState('done'); reportToAgent(true, r.output); }
      else { setErr(r.error); setState('error'); reportToAgent(false, r.error); }
    } catch (e) {
      setErr((e as { error?: string }).error ?? 'Falha na consulta.');
      setState('error');
    }
  }

  return (
    <div className="chat-dispatch-card">
      <div className="chat-dispatch-head">
        <span className="chat-dispatch-kind chat-dispatch-suggestion">banco · {req.env}</span>
        <strong>Consulta ao banco</strong>
      </div>
      <pre className="chat-doc-preview">{req.query}</pre>
      {state === 'done' ? (
        <pre className="chat-doc-preview" style={{ maxHeight: 360 }}>{output}</pre>
      ) : state === 'confirm' ? (
        <div className="chat-dispatch-actions">
          <span className="chat-error">⚠️ Escrita — confirme para executar.</span>
          <button type="button" onClick={() => run(true)}>✍️ Confirmar escrita</button>
          <button type="button" onClick={trustAndRun} title="Executa e dispensa a confirmação de escrita neste ambiente daqui pra frente (exceto prod).">🤝 Confiar sempre aqui</button>
        </div>
      ) : (
        <div className="chat-dispatch-actions">
          <button type="button" onClick={() => run(false)} disabled={state === 'running'}>
            {state === 'running' ? 'consultando…' : '🗄️ Executar consulta'}
          </button>
          {state === 'error' && <span className="chat-error">{err}</span>}
        </div>
      )}
    </div>
  );
}

/**
 * Cartão: PROPOSTA de acesso a banco. Um agente curador define a allowlist
 * (operações + tabelas) de um ambiente; o usuário REVISA e aprova → vira grant ativo.
 * É a camada de governança: o broker só libera o que está aprovado aqui.
 */
function GrantProposalCard({ slug, agentId, req, onApproved }: { slug: string; agentId?: string | undefined; req: GrantProposalReq; onApproved?: ((text: string) => void) | undefined }) {
  const [state, setState] = useState<'idle' | 'saving' | 'running' | 'confirm' | 'done' | 'error'>('idle');
  const [trusted, setTrusted] = useState(false);
  const [output, setOutput] = useState('');
  const [err, setErr] = useState('');
  const envIdRef = useRef<string | null>(null);
  const ops = req.operations.length > 0 ? req.operations : ['select'];
  const writes = ops.some((o) => o !== 'select');

  // Executa a query embutida no grant (se houver) assim que o acesso é liberado —
  // resolve "de primeira" sem depender de outro turno do agente.
  async function runEmbedded(envId: string, confirm: boolean) {
    if (!req.query || !agentId) return false;
    setState('running');
    const r = await api.dbQuery(slug, { agentId, environmentId: envId, query: req.query, confirm });
    if (r.ok) { setOutput(r.output); setState('done'); onApproved?.(dbResultMessage(req.env, true, r.output)); return true; }
    if (r.needsConfirm) { setState('confirm'); return true; }
    setErr(r.error); setState('error'); onApproved?.(dbResultMessage(req.env, false, r.error)); return true;
  }

  // trust=true → "Confiar sempre aqui": grant sem confirmação de escrita (persistente,
  // por agente×ambiente). Escrita em prod ainda confirma sempre (regra do broker).
  async function approve(trust: boolean) {
    if (!agentId) { setErr('Sem agente nesta conversa.'); setState('error'); return; }
    setState('saving'); setErr('');
    try {
      const { environments } = await api.listEnvironments(slug);
      const wanted = req.env.trim().toLowerCase();
      const env = environments.find((e) => (e.tier ?? '').toLowerCase() === wanted || e.name.toLowerCase() === wanted)
        ?? environments.find((e) => e.name.toLowerCase().includes(wanted));
      if (!env) { setErr(`Ambiente "${req.env}" não encontrado.`); setState('error'); return; }
      envIdRef.current = env.id;
      await api.saveAgentDbGrant(slug, agentId, env.id, {
        operations: ops,
        tables: req.tables,
        maxRows: 1000,
        requireConfirmWrite: !trust,
        instructions: req.rationale ?? '',
        enabled: true,
      });
      setTrusted(trust);
      // Se o agente já disse o que vai rodar, executa AQUI (confirm=trust); senão,
      // continua a conversa pra ele prosseguir no próximo turno.
      if (req.query) {
        await runEmbedded(env.id, trust);
      } else {
        setState('done');
        const tablesLabel = req.tables.includes('*') ? 'todas as tabelas' : req.tables.join(', ');
        onApproved?.(`✅ Acesso ${trust ? 'confiado' : 'aprovado'} no ambiente ${req.env} (${ops.join(', ')} em ${tablesLabel}). Pode prosseguir com o que pedi.`);
      }
    } catch (e) {
      setErr((e as { error?: string }).error ?? 'Falha ao aprovar.');
      setState('error');
    }
  }

  // Confirmação de escrita do query embutido: roda confirmado; trust também promove o grant.
  async function confirmWrite(trust: boolean) {
    if (!envIdRef.current || !agentId) return;
    try {
      if (trust) {
        const { grants } = await api.listAgentDbGrants(slug, agentId);
        const g = grants.find((x) => x.environmentId === envIdRef.current);
        if (g) await api.saveAgentDbGrant(slug, agentId, envIdRef.current, { operations: g.operations, tables: g.tables, maxRows: g.maxRows, requireConfirmWrite: false, instructions: g.instructions, enabled: true });
        setTrusted(true);
      }
      await runEmbedded(envIdRef.current, true);
    } catch (e) {
      setErr((e as { error?: string }).error ?? 'Falha na consulta.'); setState('error');
    }
  }

  const busy = state === 'saving' || state === 'running';
  return (
    <div className="chat-dispatch-card">
      <div className="chat-dispatch-head">
        <span className={`chat-dispatch-kind ${writes ? 'chat-dispatch-bug' : 'chat-dispatch-suggestion'}`}>acesso a dados · {req.env}</span>
        <strong>Proposta de acesso ao banco</strong>
      </div>
      {req.rationale && <p style={{ margin: '4px 0', color: 'var(--muted)' }}>{req.rationale}</p>}
      <div className="chat-doc-preview">
        <div><b>Operações:</b> {ops.join(', ')}{writes ? ' (escrita exige confirmação)' : ''}</div>
        <div><b>Tabelas:</b> {req.tables.includes('*') ? 'TODAS (*)' : req.tables.join(', ') || '(nenhuma)'}</div>
      </div>
      {req.query && <pre className="chat-doc-preview" style={{ marginTop: 6 }}>{req.query}</pre>}
      {state === 'done' ? (
        <>
          {output && <pre className="chat-doc-preview" style={{ maxHeight: 360 }}>{output}</pre>}
          <div className="chat-dispatch-actions">
            <span>{trusted ? '🤝 Ambiente confiado — próximas operações aqui rodam sem pedir.' : (output ? '✅ Executado.' : '✅ Acesso aprovado para este ambiente.')}</span>
          </div>
        </>
      ) : state === 'confirm' ? (
        <div className="chat-dispatch-actions">
          <span className="chat-error">⚠️ Escrita — confirme para executar.</span>
          <button type="button" onClick={() => confirmWrite(false)}>✍️ Confirmar escrita</button>
          <button type="button" onClick={() => confirmWrite(true)} title="Executa e dispensa a confirmação de escrita neste ambiente daqui pra frente (exceto prod).">🤝 Confiar sempre aqui</button>
        </div>
      ) : (
        <div className="chat-dispatch-actions">
          <button type="button" onClick={() => approve(false)} disabled={busy}>
            {state === 'saving' ? 'aprovando…' : state === 'running' ? 'executando…' : '✅ Aprovar acesso'}
          </button>
          <button type="button" onClick={() => approve(true)} disabled={busy} title="Libera o acesso e dispensa confirmação de escrita neste ambiente (exceto prod). Fica lembrado.">
            🤝 Confiar sempre aqui
          </button>
          {state === 'error' && <span className="chat-error">{err}</span>}
        </div>
      )}
    </div>
  );
}

/** Cartão: salvar um documento proposto pelo agente na base de conhecimento (docs/). */
function DocSaveCard({ slug, doc }: { slug: string; doc: ProposedDoc }) {
  const [state, setState] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');
  const [open, setOpen] = useState(false);
  async function save() {
    setState('saving');
    try {
      await api.saveDoc(slug, doc.path, doc.content);
      setState('done');
    } catch {
      setState('error');
    }
  }
  return (
    <div className="chat-dispatch-card">
      <div className="chat-dispatch-head">
        <span className="chat-dispatch-kind chat-dispatch-suggestion">doc</span>
        <strong>{doc.path}</strong>
        <button type="button" className="btn-ghost-sm" onClick={() => setOpen((o) => !o)} style={{ marginLeft: 'auto' }}>
          {open ? 'ocultar' : 'ver'}
        </button>
      </div>
      {open && <pre className="chat-doc-preview">{doc.content}</pre>}
      {state === 'done' ? (
        <div className="chat-dispatch-ok">✅ Salvo em {doc.path}. <Link to={`/p/${slug}/knowledge`}>ver na base →</Link></div>
      ) : (
        <div className="chat-dispatch-actions">
          <button type="button" onClick={save} disabled={state === 'saving'}>{state === 'saving' ? 'salvando…' : '💾 Salvar na base'}</button>
          {state === 'error' && <span className="chat-error">falhou</span>}
        </div>
      )}
    </div>
  );
}

/** Cartão: busca os logs de um container e mostra inline na conversa. */
function LogsRequestCard({ slug, req }: { slug: string; req: LogsReq }) {
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [lines, setLines] = useState<Array<{ id: number; ts: string; message: string }>>([]);
  const [note, setNote] = useState('');

  async function fetchLogs() {
    setState('loading');
    try {
      const { sources } = await api.listLogSources(slug);
      const src = sources.find((s) => s.containerName === req.container);
      if (!src) {
        setNote(`Container "${req.container}" não está cadastrado em Logs.`);
        setState('error');
        return;
      }
      const { lines } = await api.getLogLines(slug, src.id, { day: req.day, q: req.q, limit: 200, order: 'desc' });
      setLines(lines);
      setState('done');
    } catch {
      setState('error');
      setNote('Falha ao buscar logs.');
    }
  }

  return (
    <div className="chat-dispatch-card">
      <div className="chat-dispatch-head">
        <span className="chat-dispatch-kind chat-dispatch-suggestion">logs</span>
        <strong>{req.container}{req.day ? ` · ${req.day}` : ''}{req.q ? ` · "${req.q}"` : ''}</strong>
      </div>
      {state !== 'done' ? (
        <div className="chat-dispatch-actions">
          <button type="button" onClick={fetchLogs} disabled={state === 'loading'}>
            {state === 'loading' ? 'buscando…' : '📜 Ver logs'}
          </button>
          {note && <span className="chat-error">{note}</span>}
        </div>
      ) : lines.length === 0 ? (
        <div className="users-muted">Sem linhas no período.</div>
      ) : (
        <div className="logs-lines" style={{ maxHeight: 240 }}>
          {lines.map((l) => (
            <div key={l.id} className="logs-line">
              <span className="logs-line-ts">{new Date(l.ts).toLocaleTimeString()}</span>
              <span className="logs-line-msg">{l.message}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Cartão para subir/derrubar ambiente efêmero a partir da conversa. */
function EnvActionCard({ slug, action }: { slug: string; action: { type: 'up'; branch: string; ttlMinutes?: number | undefined } | { type: 'down'; envId: string } }) {
  if (action.type === 'up') return <EnvUpCard slug={slug} branch={action.branch} ttlMinutes={action.ttlMinutes} />;
  return <EnvDownCard slug={slug} envId={action.envId} />;
}

/**
 * Card de subir ambiente que reflete o ESTADO real do efêmero (por branch):
 * consulta na montagem (persiste no refresh) e faz polling enquanto sobe; quando
 * está de pé, o botão "Subir" vira o link "Acessar ambiente".
 */
function EnvUpCard({ slug, branch, ttlMinutes }: { slug: string; branch: string; ttlMinutes?: number | undefined }) {
  const [env, setEnv] = useState<EphemeralEnv | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    async function tick() {
      try {
        const { environments } = await api.listEphemeral(slug);
        if (!alive) return;
        const match = environments.find((e) => e.branch === branch && e.status !== 'destroyed') ?? null;
        setEnv(match);
        if (match && (match.status === 'creating' || match.status === 'stopping')) {
          timer = setTimeout(tick, 4000);
        }
      } catch {
        /* ignore */
      } finally {
        if (alive) setLoading(false);
      }
    }
    void tick();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [slug, branch, nonce]);

  async function subir() {
    setBusy(true);
    setErr(null);
    try {
      await api.createEphemeral(slug, { branch, mode: 'manual', ttlMinutes });
      setNonce((n) => n + 1); // re-consulta e começa o polling
    } catch (e) {
      const c = (e as { error?: string }).error;
      setErr(c === 'no_free_port' ? 'Sem porta livre.' : c === 'ephemeral_not_configured' ? 'Efêmero não configurado.' : 'Falha ao subir.');
    } finally {
      setBusy(false);
    }
  }

  // Atualiza o efêmero EXISTENTE no lugar (puxa o commit novo, mesma URL).
  async function atualizar() {
    if (!env) return;
    setBusy(true);
    setErr(null);
    try {
      await api.redeployEphemeral(slug, env.id);
      setNonce((n) => n + 1); // status volta a 'creating' e o polling acompanha
    } catch {
      setErr('Falha ao atualizar.');
    } finally {
      setBusy(false);
    }
  }

  const status = env?.status;
  return (
    <div className="chat-dispatch-card">
      <div className="chat-dispatch-head">
        <span className="chat-dispatch-kind chat-dispatch-suggestion">ambiente</span>
        <strong>Ambiente — {branch}</strong>
      </div>
      {loading ? (
        <div className="chat-dispatch-actions"><span style={{ color: 'var(--muted)' }}>verificando…</span></div>
      ) : status === 'running' && env?.url ? (
        <div className="chat-dispatch-ok">
          <a href={env.url} target="_blank" rel="noreferrer">🌐 Acessar ambiente →</a>
          {' · '}
          <button type="button" onClick={atualizar} disabled={busy} title="Puxa o commit novo da branch e rebuilda, mantendo esta mesma URL.">{busy ? 'atualizando…' : '🔄 Atualizar'}</button>
          {' · '}
          <Link to={`/p/${slug}/ephemeral`}>Efêmeros</Link>
          {err && <span className="chat-error"> {err}</span>}
        </div>
      ) : status === 'creating' ? (
        <div className="chat-dispatch-actions">
          <span style={{ color: 'var(--muted)' }}>⏳ subindo… (alguns minutos)</span>
          <Link to={`/p/${slug}/ephemeral`}>ver progresso</Link>
        </div>
      ) : status === 'stopping' ? (
        <div className="chat-dispatch-actions"><span style={{ color: 'var(--muted)' }}>derrubando…</span></div>
      ) : status === 'error' ? (
        <div className="chat-dispatch-actions">
          <span className="chat-error">falhou ao subir</span>
          <button type="button" onClick={subir} disabled={busy}>{busy ? 'processando…' : 'tentar de novo'}</button>
          <Link to={`/p/${slug}/ephemeral`}>detalhes</Link>
        </div>
      ) : (
        <div className="chat-dispatch-actions">
          <button type="button" onClick={subir} disabled={busy}>{busy ? 'processando…' : '🚀 Subir ambiente'}</button>
          {err && <span className="chat-error">{err}</span>}
        </div>
      )}
    </div>
  );
}

function EnvDownCard({ slug, envId }: { slug: string; envId: string }) {
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  async function run() {
    setState('sending');
    try {
      await api.destroyEphemeral(slug, envId);
      setState('done');
    } catch {
      setState('error');
    }
  }
  return (
    <div className="chat-dispatch-card">
      <div className="chat-dispatch-head">
        <span className="chat-dispatch-kind chat-dispatch-suggestion">derrubar</span>
        <strong>Derrubar ambiente {envId.slice(0, 8)}…</strong>
      </div>
      {state === 'done' ? (
        <div className="chat-dispatch-ok">✅ Ambiente sendo derrubado. <Link to={`/p/${slug}/ephemeral`}>Efêmeros →</Link></div>
      ) : (
        <div className="chat-dispatch-actions">
          <button type="button" onClick={run} disabled={state === 'sending'}>{state === 'sending' ? 'processando…' : '🗑️ Derrubar'}</button>
          {state === 'error' && <span className="chat-error">falhou</span>}
        </div>
      )}
    </div>
  );
}

/** Cartão de confirmação: o usuário despacha a mudança proposta (vira report → PR). */
function DispatchCard({ proposal, threadId, slug, dispatchedReportId }: { proposal: DispatchProposal; threadId: string; slug?: string | undefined; dispatchedReportId?: string | undefined }) {
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [reportId, setReportId] = useState<string | null>(null);
  // Já despachado (detectado pela msg de sistema, persiste no reload) OU despachado agora.
  const effectiveReportId = dispatchedReportId ?? reportId;

  async function dispatch() {
    setState('sending');
    try {
      const { reportId } = await api.dispatchFromThread(threadId, proposal);
      setReportId(reportId);
      setState('done');
    } catch {
      setState('error');
    }
  }

  return (
    <div className="chat-dispatch-card">
      <div className="chat-dispatch-head">
        <span className={`chat-dispatch-kind chat-dispatch-${proposal.kind}`}>
          {proposal.kind === 'bug' ? 'correção' : 'melhoria'}
        </span>
        <strong>{proposal.title}</strong>
      </div>
      <div className="chat-dispatch-desc">{proposal.description}</div>
      {proposal.baseBranch && (
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>🌿 base: <code>{proposal.baseBranch}</code></div>
      )}
      {proposal.wantsEphemeral && (
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>🚀 sobe o ambiente automaticamente quando o código ficar pronto</div>
      )}
      {effectiveReportId ? (
        <ReportTracker slug={slug} reportId={effectiveReportId} />
      ) : (
        <div className="chat-dispatch-actions">
          <button type="button" onClick={dispatch} disabled={state === 'sending'}>
            {state === 'sending' ? 'despachando…' : '🚀 Despachar (codar na branch, sem PR)'}
          </button>
          {state === 'error' && <span className="chat-error">falha ao despachar</span>}
        </div>
      )}
    </div>
  );
}

/**
 * Acompanha um report despachado AO VIVO no chat (polling) e oferece as ações
 * inline (subir ambiente, acessar, abrir PR) — pra fazer tudo sem ir ao board.
 */
function ReportTracker({ slug, reportId }: { slug?: string | undefined; reportId: string }) {
  const [report, setReport] = useState<Report | null>(null);
  const [eph, setEph] = useState<EphemeralEnv | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!slug) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    async function tick() {
      try {
        const { report: r } = await api.getReport(slug!, reportId);
        if (!alive) return;
        setReport(r);
        if (r.branchName) {
          try {
            const { environments } = await api.listEphemeral(slug!);
            if (alive) setEph(environments.find((x) => x.branch === r.branchName && x.status !== 'destroyed') ?? null);
          } catch {
            /* lista de efêmeros indisponível neste tick */
          }
        }
        // Polla enquanto não-terminal (committed inclusive) — assim reflete o efêmero
        // subir / ficar pronto por qualquer via (auto, chat ou board), sem recarregar.
        const terminal = ['pr_open', 'in_review', 'resolved', 'rejected'].includes(r.status);
        if (alive && !terminal) timer = setTimeout(tick, 5000);
      } catch {
        if (alive) timer = setTimeout(tick, 8000); // re-tenta mesmo em erro de rede
      }
    }
    void tick();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [slug, reportId, nonce]);

  async function subir() {
    if (!slug || !report?.branchName) return;
    setBusy(true);
    setMsg('');
    try {
      await api.createEphemeral(slug, { branch: report.branchName, mode: 'manual' });
      setNonce((n) => n + 1);
    } catch (e) {
      const c = (e as { error?: string }).error;
      setMsg(c === 'no_free_port' ? 'Sem porta livre.' : c === 'ephemeral_not_configured' ? 'Efêmero não configurado.' : 'Falha ao subir.');
    } finally {
      setBusy(false);
    }
  }

  async function atualizar() {
    if (!slug || !eph) return;
    setBusy(true);
    setMsg('');
    try {
      await api.redeployEphemeral(slug, eph.id);
      setNonce((n) => n + 1);
    } catch {
      setMsg('Falha ao atualizar.');
    } finally {
      setBusy(false);
    }
  }

  async function abrirPr() {
    if (!slug) return;
    setBusy(true);
    setMsg('');
    try {
      await api.openReportPr(slug, reportId);
      setNonce((n) => n + 1);
    } catch {
      setMsg('Falha ao abrir o PR.');
    } finally {
      setBusy(false);
    }
  }

  const boardLink = slug ? <Link to={`/p/${slug}/r/${reportId}`}>board →</Link> : null;

  if (!report) return <div className="chat-dispatch-ok">⏳ despachado, carregando… {boardLink}</div>;
  if (report.status === 'rejected') {
    return <div className="chat-dispatch-ok"><span className="chat-error">❌ rejeitado{report.triageNotes ? `: ${report.triageNotes}` : ''}</span> · {boardLink}</div>;
  }
  if (report.status === 'pr_open' || report.status === 'in_review' || report.status === 'resolved') {
    return (
      <div className="chat-dispatch-ok">
        ✅ PR aberto.{' '}
        {report.prUrl && <a href={report.prUrl} target="_blank" rel="noreferrer">ver PR →</a>} · {boardLink}
      </div>
    );
  }
  if (['queued', 'triaging', 'dispatched'].includes(report.status)) {
    return <div className="chat-dispatch-ok">⏳ codando na branch… {boardLink}</div>;
  }
  // committed
  return (
    <div className="rt-committed">
      <span>✅ Código pronto na branch <code>{report.branchName}</code> (sem PR).</span>
      <div className="rt-actions">
        {eph?.status === 'running' && eph.url ? (
          <>
            <a className="rt-btn" href={eph.url} target="_blank" rel="noreferrer">🌐 Acessar</a>
            <button type="button" className="rt-btn" disabled={busy} onClick={atualizar}>🔄 Atualizar</button>
          </>
        ) : eph?.status === 'creating' ? (
          <span className="rt-status">⏳ ambiente subindo… (1º build leva alguns min)</span>
        ) : (
          <button type="button" className="rt-btn" disabled={busy} onClick={subir}>🚀 Subir ambiente</button>
        )}
        <button type="button" className="rt-btn rt-btn-primary" disabled={busy || report.openPrRequested} onClick={abrirPr}>
          {report.openPrRequested ? 'Abrindo PR…' : '✅ Validei — abrir PR'}
        </button>
      </div>
      {msg && <span className="chat-error">{msg}</span>}
      <span className="rt-board">{boardLink}</span>
    </div>
  );
}

/** Dropdown bonito de escolha de agente da nova conversa (avatar + nome + descrição). */
function AgentPicker({ slug, agents, value, onChange }: { slug: string; agents: Agent[]; value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const sel = agents.find((a) => a.id === value);
  const triggerLabel = value === '__router__' ? 'Recepcionista' : value === '' ? 'Chat geral do projeto' : sel?.name ?? 'Selecionar agente';

  return (
    <div className="agent-picker" ref={ref}>
      <button type="button" className="agent-picker-trigger" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open}>
        <span className="agent-picker-ava">
          {value === '__router__' ? <span className="agent-picker-emoji">🧭</span>
            : value === '' ? <span className="agent-picker-emoji">💬</span>
            : sel ? <AgentAvatar slug={slug} agent={sel} size={24} /> : <span className="agent-picker-emoji">🧭</span>}
        </span>
        <span className="agent-picker-label">{triggerLabel}</span>
        <span className="agent-picker-caret">▾</span>
      </button>
      {open && (
        <div className="agent-picker-menu" role="listbox">
          <button type="button" className={`agent-picker-item ${value === '__router__' ? 'sel' : ''}`} onClick={() => { onChange('__router__'); setOpen(false); }}>
            <span className="agent-picker-emoji">🧭</span>
            <span className="agent-picker-text"><strong>Recepcionista</strong><small>escolhe o agente certo pra você</small></span>
          </button>
          {agents.map((a) => (
            <button key={a.id} type="button" className={`agent-picker-item ${value === a.id ? 'sel' : ''}`} onClick={() => { onChange(a.id); setOpen(false); }}>
              <AgentAvatar slug={slug} agent={a} size={26} />
              <span className="agent-picker-text"><strong>{a.name}</strong>{a.description ? <small>{a.description}</small> : null}</span>
            </button>
          ))}
          <button type="button" className={`agent-picker-item ${value === '' ? 'sel' : ''}`} onClick={() => { onChange(''); setOpen(false); }}>
            <span className="agent-picker-emoji">💬</span>
            <span className="agent-picker-text"><strong>Chat geral do projeto</strong><small>pergunta sobre o código</small></span>
          </button>
        </div>
      )}
    </div>
  );
}

function StreamingView({ state }: { state: StreamingAssistantState }) {
  // Esconde os blocos de ação enquanto faz stream (viram cartões só na msg final).
  const text = parseActions(state.text).text;
  // Cronômetro do turno: conta desde que este bloco aparece (início do turno) até sumir.
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const secs = Math.max(0, Math.floor((now - startedAt) / 1000));
  const elapsed = `${Math.floor(secs / 60)}:${(secs % 60).toString().padStart(2, '0')}`;
  return (
    <div className="chat-msg chat-msg-assistant chat-streaming">
      <div className="chat-msg-role">
        assistente <span style={{ color: 'var(--muted)', fontVariantNumeric: 'tabular-nums', marginLeft: 6 }} title="tempo deste turno">⏱ {elapsed}</span>
      </div>
      <div className="chat-msg-content">
        {text || <span style={{ color: 'var(--muted)' }}>pensando…</span>}
        <span className="chat-cursor">▍</span>
      </div>
      {state.tools.length > 0 && (
        <div className="chat-tool-list">
          {state.tools.map((t, i) => (
            <span key={i} className="chat-tool-chip">
              {t.name} {t.ok === false ? '⚠' : t.ok ? '✓' : '…'}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

async function consumeSse(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: string, data: Record<string, unknown>) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buf = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    for (;;) {
      const sep = buf.indexOf('\n\n');
      if (sep < 0) break;
      const chunk = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      let ev = 'message';
      const dataLines: string[] = [];
      for (const line of chunk.split('\n')) {
        if (line.startsWith(':')) continue;
        if (line.startsWith('event:')) ev = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
      }
      if (dataLines.length === 0) continue;
      try {
        const data = JSON.parse(dataLines.join('\n')) as Record<string, unknown>;
        onEvent(ev, data);
      } catch {
        /* ignore malformed */
      }
    }
  }
}

function formatRelative(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (m < 1) return 'agora';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}
